import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  TextField,
  Grid,
  Divider,
  Alert,
  FormHelperText,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip
} from '@mui/material';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useCheckout } from '../../contexts/CheckoutContext';
import BogoAttendeeFields from './BogoAttendeeFields';

/**
 * BankTransferStep Component
 * Handles both bank transfers and wire transfers depending on the selected payment method
 */
const BankTransferStep = () => {
  // Access checkout context
  const {
    customerInfo,
    updateCustomerInfo,
    paymentMethod,
    setCurrentStep,
    paymentOptionsData,
    calculateAmount,
    calculateSeparateRoomsSurcharge,
    submitCheckout,
    loading,
    error,
    // BOGO state
    pricingType,
    setPricingType,
    bogoData,
    updateBogoData
  } = useCheckout();

  // Form state
  const [formData, setFormData] = useState({
    firstName: customerInfo.firstName || '',
    lastName: customerInfo.lastName || '',
    email: customerInfo.email || '',
    phone: customerInfo.phone || '',
    address: customerInfo.address || '',
    city: customerInfo.city || '',
    postalCode: customerInfo.postalCode || '',
    transferDate: customerInfo.transferDate || null,
    transferReference: customerInfo.transferReference || ''
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Get bank details based on customer location and payment method
  const getBankDetails = () => {
    const { country, province } = customerInfo;

    if (country === 'canada') {
      return paymentOptionsData?.canada?.[province]?.bankTransfer || '';
    } else if (country === 'usa') {
      return paymentOptionsData?.usa?.default?.bankTransfer || '';
    } else {
      return paymentOptionsData?.other?.default?.wireTransfer || '';
    }
  };

  // Get formatted payment amount
  // pricingOverride allows displaying prices for specific pricing types
  const getFormattedAmount = (pricingOverride = null) => {
    const { country, province, state } = customerInfo;
    const amount = calculateAmount(country, province || state, pricingOverride);

    if (country === 'canada') {
      // For Canadian provinces, display CAD with tax included
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'CAD'
      }).format(amount);

      return `${formattedAmount} (Tax Included)`;
    } else {
      // For USA and Other, display both CAD and USD
      const cadAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'CAD'
      }).format(amount);

      // Calculate approximate USD amount (using approximate exchange rate)
      const usdAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount * 0.73); // Using approximate CAD to USD conversion

      return (
        <>
          {cadAmount} (Tax Included)<br />
          <span style={{ fontSize: '0.9em', color: '#666' }}>≈ {usdAmount} USD</span>
        </>
      );
    }
  };

  // Get simple formatted price for radio button labels
  const getSimplePrice = (pricingOverride, accommodationOverride = null) => {
    const { country, province, state } = customerInfo;
    const amount = calculateAmount(country, province || state, pricingOverride, accommodationOverride);
    const currency = country === 'canada' ? 'CAD' : 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Get formatted surcharge amount for separate rooms
  const getFormattedSurcharge = () => {
    const { country, province, state } = customerInfo;
    const surcharge = calculateSeparateRoomsSurcharge(country, province || state);
    const currency = country === 'canada' ? 'CAD' : 'USD';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(surcharge.total);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      transferDate: date
    });

    // Clear error for this field
    if (errors.transferDate) {
      setErrors({
        ...errors,
        transferDate: ''
      });
    }
  };

  // Handle back button
  const handleBack = () => {
    // Save current form data to context
    updateCustomerInfo(formData);
    
    // Go back to payment options
    setCurrentStep(1);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Basic validation for required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9]{10,15}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal/ZIP code is required';
    } else {
      // Validate postal code format based on country
      const { country } = customerInfo;
      const postalCode = formData.postalCode.trim();

      if (country === 'canada' && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode)) {
        newErrors.postalCode = 'Invalid Canadian postal code (format: A1A 1A1)';
      } else if (country === 'usa' && !/^\d{5}(-\d{4})?$/.test(postalCode)) {
        newErrors.postalCode = 'Invalid US ZIP code (format: 12345 or 12345-6789)';
      }
    }

    if (!formData.transferDate) {
      newErrors.transferDate = 'Expected transfer date is required';
    }

    // BOGO validation
    if (pricingType === 'bogo') {
      if (!bogoData.attendee1FirstName?.trim()) {
        newErrors.attendee1FirstName = 'Attendee 1 first name is required';
      }
      if (!bogoData.attendee1LastName?.trim()) {
        newErrors.attendee1LastName = 'Attendee 1 last name is required';
      }
      if (!bogoData.attendee2FirstName?.trim()) {
        newErrors.attendee2FirstName = 'Attendee 2 first name is required';
      }
      if (!bogoData.attendee2LastName?.trim()) {
        newErrors.attendee2LastName = 'Attendee 2 last name is required';
      }
      if (!bogoData.accommodationPreference) {
        newErrors.accommodationPreference = 'Please select an accommodation preference';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Verify all required fields are present
      const requiredFields = [
        'firstName', 'lastName', 'email', 'phone', 'address', 'city', 'postalCode', 'transferDate'
      ];
      
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        setErrors(prev => ({
          ...prev,
          ...missingFields.reduce((acc, field) => ({ 
            ...acc, 
            [field]: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
          }), {})
        }));
        return;
      }
      
      // Normalize all text fields to lowercase for consistent case handling
      // Preserves internal spaces, only trims leading/trailing whitespace
      const processedFormData = {
        firstName: formData.firstName.trim().toLowerCase(),
        lastName: formData.lastName.trim().toLowerCase(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim().toLowerCase(),
        address: formData.address.trim().toLowerCase(),
        city: formData.city.trim().toLowerCase(),
        postalCode: formData.postalCode.trim(), // Postal code unchanged per requirements
        transferDate: formData.transferDate,
        transferReference: formData.transferReference ? formData.transferReference.trim().toLowerCase() : ''
      };

      // Update customer info in context
      // Make sure to preserve the country, province, and state from the LocationStep
      const updatedInfo = {
        ...processedFormData,
        country: customerInfo.country,
        province: customerInfo.province,
        state: customerInfo.state
      };
      
      // Make sure all required fields have a value to prevent validation errors
      Object.keys(updatedInfo).forEach(key => {
        if (updatedInfo[key] === undefined || updatedInfo[key] === null) {
          updatedInfo[key] = '';
        }
      });
      
      console.log('Bank Transfer: Submitting with customer data:', updatedInfo);
      // Also update context for display purposes
      updateCustomerInfo(updatedInfo);

      try {
        // Pass data directly to avoid React state timing issues
        await submitCheckout(updatedInfo);
      } catch (err) {
        // Error handling is managed in the checkout context
        console.error('Bank Transfer: Submission error:', err);
      }
    }
  };

  const bankDetails = getBankDetails();
  const isWireTransfer = paymentMethod === 'wireTransfer';

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {isWireTransfer ? 'International Wire Transfer' : 'Bank Transfer Information'}
        </Typography>

        <Box sx={{ my: 3, p: 3, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            Bank Details
          </Typography>
          <Box component="pre" sx={{ 
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflowX: 'auto'
          }}>
            {bankDetails}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" component="h3" gutterBottom>
          Payment Total: <strong>{getFormattedAmount()}</strong>
        </Typography>

        {/* Pricing Type Selection */}
        <Box sx={{ my: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Registration Type
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={pricingType}
              onChange={(e) => setPricingType(e.target.value)}
            >
              <FormControlLabel
                value="standard"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Standard Registration (1 Person)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSimplePrice('standard')} - Single attendee
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="bogo"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">
                        BOGO - Bring a Friend! (2 People)
                      </Typography>
                      <Chip
                        label="Save 25% per person!"
                        color="success"
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {getSimplePrice('bogo', '')} - Buy one, get one 50% off!
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* BOGO Attendee Fields */}
        {pricingType === 'bogo' && (
          <BogoAttendeeFields
            bogoData={bogoData}
            onUpdateBogoData={updateBogoData}
            errors={errors}
            formattedSurcharge={getFormattedSurcharge()}
          />
        )}

        <Divider sx={{ my: 3 }} />

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="address"
                label="Street Address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="city"
                label="City"
                value={formData.city}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="postalCode"
                label="Postal/ZIP Code"
                value={formData.postalCode}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.postalCode}
                helperText={errors.postalCode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="transferDate"
                label="Expected Transfer Date"
                type="date"
                value={formData.transferDate || ''}
                onChange={(e) => handleDateChange(e.target.value)}
                fullWidth
                required
                error={!!errors.transferDate}
                helperText={errors.transferDate || 'Format: YYYY-MM-DD'}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="transferReference"
                label="Transfer Reference/Notes"
                value={formData.transferReference}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
              disabled={loading}
            >
              Back to Payment Options
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Complete Purchase
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BankTransferStep;